import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import { Link } from "react-router-dom";
import useSubAdminData from "Views/people/SubAdmin/data/subAdminData";
import { useState, useEffect } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { baseURL } from "utils/api";
import axios from "utils/api";
import { toast } from "react-toastify";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { Dialog, DialogTitle, DialogContent, DialogActions, InputLabel, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlined from "@mui/icons-material/LockOutlined";

function Tables() {
  const [refresh, setRefresh] = useState(false); // trigger table refresh
  const [searchQuery, setSearchQuery] = useState(""); // search state
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }, [passwordUser]);

  useEffect(() => {
    let active = true;
    axios.get(`${baseURL}/user/profile`).then((res) => {
      if (active) setIsSuperAdmin(res.data.success && res.data.data?.role === "SUPER_ADMIN");
    }).catch(() => {
      if (active) setIsSuperAdmin(false);
    });
    return () => { active = false; };
  }, []);

  const closePasswordDialog = () => {
    if (passwordSaving) return;
    setPasswordUser(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    if (!isSuperAdmin || !passwordUser || passwordSaving) return;
    if (newPassword.trim().length < 6 || new TextEncoder().encode(newPassword).length > 72) {
      toast.error("Password must have at least 6 characters and be no more than 72 bytes");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await axios.put(`${baseURL}/user/${passwordUser._id}/password`, { newPassword });
      if (!res.data.success) {
        toast.error(res.data.message || "Failed to update password");
        return;
      }
      toast.success(res.data.message || "Password updated successfully");
      setPasswordUser(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || "Failed to update password");
    } finally {
      setPasswordSaving(false);
    } 
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Handle status toggle
  const handleStatusToggle = (id, newStatus) => {
    // const token = localStorage.getItem("token");
    axios
      .put(`${baseURL}/user/${id}`, { isActive: newStatus })
      .then((res) => {
        console.log("✅ Sub-admin status updated:", res.data);
        toast.success(`Sub-admin ${newStatus ? "activated" : "deactivated"} successfully!`);
        setRefresh((prev) => !prev); // refresh table data
      })
      .catch((err) => {
        toast.error("Failed to update sub-admin status. Please try again.");
        console.error("❌ Error updating sub-admin status:", err);
        console.error("❌ Error response:", err.response?.data);
      });
  };

  const { columns, rows, loading, totalItems } = useSubAdminData(
    handleStatusToggle,
    refresh,
    currentPage,
    rowsPerPage,
    searchQuery,
    isSuperAdmin ? setPasswordUser : undefined
  );

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const tabsData = [
    { label: "All", count: 92, color: "#2027e1ff", content: "" },
    { label: "Active", count: 12, color: "#ec3c10ff", content: "" },
    { label: "Hold", count: 22, color: "#ec10c7ff", content: "" },
    { label: "Completed", count: 72, color: "#10a6ecff", content: "" },
    { label: "Discontinued", count: 88, color: "#1a6808b7", content: "" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                <ArgonTypography mb={2} variant="h6">
                  All Sub-Admin List
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="Search by name or email"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ margin: "10px" }}
                  />
                  <ArgonButton
                    color="primary"
                    component={Link}
                    to="/subadmin/adduser"
                    variant="contained"
                    size="small"
                  >
                    +Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching sub-admins...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {searchQuery
                      ? "No sub-admins found matching your search"
                      : "No sub-admins found"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {searchQuery
                      ? "Try adjusting your search terms."
                      : "No sub-admin data available."}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {totalItems > rowsPerPage && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      <Dialog
        open={Boolean(passwordUser) && isSuperAdmin}
        onClose={closePasswordDialog}
        fullWidth
        maxWidth="xs"
        aria-labelledby="subadmin-password-title"
        PaperProps={{ sx: { borderRadius: "20px", boxShadow: "0 8px 30px rgba(0,0,0,0.1)" } }}
      >
        <form onSubmit={handlePasswordUpdate}>
          <DialogTitle id="subadmin-password-title" sx={{ px: 3, pt: 3, pb: 1 }}>
            <ArgonTypography component="span" variant="h5" fontWeight="bold">
              Update Sub-Admin Password
            </ArgonTypography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 1 }}>
            <ArgonBox mb={3}>
              <ArgonTypography variant="body2" fontWeight="medium">
                {passwordUser?.fullName}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="text" sx={{ overflowWrap: "anywhere" }}>
                {passwordUser?.email}
              </ArgonTypography>
            </ArgonBox>
            <ArgonBox mb={2.5}>
              <InputLabel htmlFor="subadmin-new-password" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                <LockOutlined color="primary" sx={{ fontSize: 18, mr: 1 }} />
                <ArgonTypography component="span" variant="body2" fontWeight="medium">
                  New Password *
                </ArgonTypography>
              </InputLabel>
              <ArgonInput
                id="subadmin-new-password"
                placeholder="Enter new password"
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                fullWidth
                disabled={passwordSaving}
                inputProps={{ disabled: passwordSaving, "aria-describedby": "subadmin-password-hint" }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      size="small"
                      aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                      aria-pressed={showNewPassword}
                      onClick={() => setShowNewPassword((visible) => !visible)}
                      onMouseDown={(event) => event.preventDefault()}
                      disabled={passwordSaving}
                    >
                      {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <ArgonTypography id="subadmin-password-hint" variant="caption" color="text">
                At least 6 characters
              </ArgonTypography>
            </ArgonBox>
            <ArgonBox mb={1}>
              <InputLabel htmlFor="subadmin-confirm-password" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                <LockOutlined color="primary" sx={{ fontSize: 18, mr: 1 }} />
                <ArgonTypography component="span" variant="body2" fontWeight="medium">
                  Confirm Password *
                </ArgonTypography>
              </InputLabel>
              <ArgonInput
                id="subadmin-confirm-password"
                placeholder="Re-enter new password"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                fullWidth
                disabled={passwordSaving}
                inputProps={{ disabled: passwordSaving }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      size="small"
                      aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      aria-pressed={showConfirmPassword}
                      onClick={() => setShowConfirmPassword((visible) => !visible)}
                      onMouseDown={(event) => event.preventDefault()}
                      disabled={passwordSaving}
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </ArgonBox>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1, flexWrap: "wrap" }}>
            <ArgonButton type="button" variant="outlined" color="primary" onClick={closePasswordDialog} disabled={passwordSaving}>Cancel</ArgonButton>
            <ArgonButton type="submit" variant="contained" color="primary" disabled={passwordSaving}>
              {passwordSaving ? "Updating..." : "Update Password"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

